import React, { HTMLInputTypeAttribute, useEffect, useState } from "react";

import "./InputField.scss";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { LayoutState } from "../../../store/reducers/layout-reducer";
import { ApplicationState } from "../../../store";
import { Typography } from "../Typography/Typography";

/**
 * Props for the InputField component.
 *
 * @description The InputField component is a form input with integrated label and error handling.
 * It supports validation states and integrates with the application's dark mode theme.
 *
 * @property {string} [id] - HTML id attribute for the input element.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 * @property {string} label - The label text displayed above the input. Required.
 *   Also used to generate the input id and default placeholder.
 * @property {string} [errorMsg=`Invalid ${label}`] - Error message displayed when input is invalid.
 * @property {string} [className] - Additional CSS class names to apply to the input element.
 * @property {boolean} [isInvalid=false] - Whether the input has failed validation.
 *   When true and input is not focused, displays error styling and message.
 * @property {string} [value] - The current value of the input field.
 * @property {string} [placeholder=`Your ${label.toLowerCase()}`] - Placeholder text for the input.
 * @property {HTMLInputTypeAttribute} [type="text"] - HTML input type attribute.
 *   Common values: "text", "password", "email", "number", "tel", "url"
 * @property {(e: React.ChangeEvent<HTMLInputElement>) => void} [onChange] - Change event handler.
 *   Called when the input value changes.
 *
 * @example
 * // Basic text input
 * <InputField
 *   label="Username"
 *   value={username}
 *   onChange={(e) => setUsername(e.target.value)}
 * />
 *
 * @example
 * // Password input with validation
 * <InputField
 *   label="Password"
 *   type="password"
 *   value={password}
 *   onChange={handlePasswordChange}
 *   isInvalid={passwordError}
 *   errorMsg="Password must be at least 8 characters"
 * />
 *
 * @example
 * // Email input with custom placeholder
 * <InputField
 *   label="Email"
 *   type="email"
 *   placeholder="Enter your email address"
 *   value={email}
 *   onChange={handleEmailChange}
 * />
 */
interface Props {
  id?: string;
  testId?: string;
  label: string;
  errorMsg?: string;
  className?: string;
  isInvalid?: boolean;
  value?: string;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * A form input component with integrated label and validation error display.
 *
 * @description InputField renders a labeled input element with support for validation states.
 * It automatically handles focus/blur states to control error message visibility and integrates
 * with the application's dark mode theme through Redux state.
 *
 * Features:
 * - Automatic label-input association via generated id
 * - Validation state styling with error message display
 * - Focus-aware error display (errors hidden while editing)
 * - Dark mode support via Redux layout state
 * - Typography component integration for consistent text styling
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A div containing label, input, and optional error message
 *
 * @see Props for all available properties
 */
const InputField: React.FC<Props> = (props) => {
  const { isDarkMode } = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const [focusElement, setFocusElement] = useState<boolean>(false);
  const {
    label,
    onChange,
    testId,
    type = "text",
    value,
    placeholder = `Your ${label?.toLocaleLowerCase()}`,
    isInvalid = false,
    errorMsg = `Invalid ${label}`,
    className,
  } = props;

  const wrapClass = classNames(className);

  /**
   * Updates input validation styling based on value and focus state.
   * Adds 'invalid' class when input is invalid and not focused.
   * Removes 'invalid' class when input is valid or focused.
   */
  useEffect(
    function updateValidationStyling() {
      const input = document.getElementById(`input-field${label}`);
      if (input) {
        if (isInvalid && !focusElement) {
          input.classList.add("invalid");
        } else {
          input.classList.remove("invalid");
        }
      }
    },
    [value, focusElement]
  );

  return (
    <div className="input-group-holder">
      <Typography variant="body3">
        <label
          className={`margin-zero login-label ${isDarkMode ? "hc" : ""}`}
          htmlFor={`input-field${label}`}
        >
          {label}
        </label>
      </Typography>
      <input
        data-testid={testId}
        className={wrapClass}
        id={`input-field${label}`}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={() => setFocusElement(true)}
        onBlur={() => setFocusElement(false)}
      ></input>
      {isInvalid && !focusElement ? (
        <Typography variant="body2" className="error-text margin-zero">
          {errorMsg}
        </Typography>
      ) : null}
    </div>
  );
};

export default InputField;
